import React, { useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@components/atoms/button';
import { useScreenFullHeight } from '@hooks/use-screen-full-height';
import {
    CameraVariantLineEnum,
    DocsVerificationTabEnum,
    DocsVerificationTypeEnum,
} from '@models/enums';
import type { FileWithPreview, ThemeProps } from '@models/types';
import { Card } from '@modules/dashboard/pages/docs-verification/docs-verification-page/card';
import { RequirementTypeEnum } from '@modules/dashboard/pages/docs-verification/docs-verification-page/requirements/types';
import {
    ALLOWED_EXTENSIONS,
    IMAGE_EXTENSIONS,
} from '@modules/dashboard/pages/docs-verification/docs-verification-page/tabs/address/constants';
import type { BiometricsProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/tabs/address/types';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/styles/useTheme';
import type { RootState } from '@store/root-reducer';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { isValidExtension } from '@utils/files/util';
import { scrollTop } from '@utils/util';
import { isTablet } from 'mobile-device-detect';

import { Timer } from '../../docs-verification-steps/start/timer';
import { TakeDocument } from '../../downloads/take-document';
import { TakeDocumentMobile } from '../../downloads/take-document-mobile';
import { usePageTitle } from '../../hooks/use-page-title';
import { useUploadFile } from '../../hooks/use-upload-file';
import { MobileHeader } from '../../mobile-header';
import { Requirements } from '../../requirements';
import { STEPS_MOBILE_TITLE } from '../constants';

import { AddressContainer } from './styles';

// eslint-disable-next-line complexity
export const Address: React.FC<BiometricsProps> = ({ isLoading, onSubmit }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`) || isTablet;
    const { isCameraMode, isShowRequirements } = useSelector(
        (state: RootState) => state.docsVerification,
    );
    const { uploadFile } = useUploadFile(ALLOWED_EXTENSIONS);
    const { pageTitle } = usePageTitle();
    const { height } = useScreenFullHeight();
    const files = useSelector((state: RootState) => state.docsVerification.documents.files).filter(
        (item) => item.tab === DocsVerificationTabEnum.ADDRESS,
    );
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isFilesLoading = !!files.filter((item) => item.status.loading).length;
    const handleFilesChange = useCallback(
        async (file: FileWithPreview) => {
            const uploadedFile = await uploadFile({
                file,
                tab: DocsVerificationTabEnum.ADDRESS,
                documentType: DocsVerificationTypeEnum.POA,
            });

            if (uploadedFile.status.success) {
                await onSubmit();
            }

            return uploadedFile;
        },
        [uploadFile, onSubmit],
    );

    const handleBackClick = () => {
        if (isCameraMode) {
            dispatch(docsVerificationSlice.actions.setIsCameraMode(false));
        }

        if (isShowRequirements) {
            dispatch(docsVerificationSlice.actions.setIsShowRequirements(false));
        } else {
            dispatch(docsVerificationSlice.actions.goBackStep());
        }
        setTimeout(() => {
            scrollTop();
        });
    };

    const isError = !!files.filter((item) => item.status.error).length;
    const isSuccess =
        files.length >= 1 && files.filter((item) => item.status.success).length === files.length;

    const handleNextClick = () => {
        if (isError) {
            dispatch(docsVerificationSlice.actions.deleteFileById(files[0].id as string));
        }
        dispatch(docsVerificationSlice.actions.setIsCameraMode(true));
    };

    const handleUploadFileClick = useCallback(async () => {
        fileInputRef.current?.click();
    }, []);

    const handleCloseCameraMode = () => {
        dispatch(docsVerificationSlice.actions.setIsCameraMode(false));
    };

    const stepsMobileTitleNode = window.document.getElementById(STEPS_MOBILE_TITLE);

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files?.[0]) {
                const file = event.target.files[0] as FileWithPreview;

                file.preview = URL.createObjectURL(file);
                const lastFile = files[files.length - 1];

                if (lastFile?.id && lastFile?.status?.error) {
                    dispatch(docsVerificationSlice.actions.deleteFileById(lastFile.id));
                    handleFilesChange(file);
                } else {
                    handleFilesChange(file);
                }
            }
        },
        [handleFilesChange, dispatch, files],
    );

    const handleRequirementsClick = () => {
        scrollTop();
        dispatch(docsVerificationSlice.actions.setIsShowRequirements(true));
    };

    const previews = useMemo(() => {
        const errorFile = files.find((item) => item.status.error);

        if (errorFile) {
            const isImage = isValidExtension(errorFile?.uploadedFile?.name || '', IMAGE_EXTENSIONS);

            return isImage ? [errorFile.uploadedFile?.preview as string] : [];
        }

        return undefined;
    }, [files]);

    return (
        <AddressContainer
            style={{
                minHeight:
                    matchesMobileSm && height
                        ? height -
                          32 -
                          (stepsMobileTitleNode?.clientHeight
                              ? stepsMobileTitleNode.clientHeight
                              : 0)
                        : undefined,
            }}
        >
            {matchesMobileSm &&
                !isShowRequirements &&
                stepsMobileTitleNode &&
                createPortal(
                    <React.Fragment>
                        <MobileHeader onBack={handleBackClick} title={pageTitle} />
                        <Timer isHidden={true} />
                    </React.Fragment>,
                    stepsMobileTitleNode,
                )}
            {isShowRequirements ? (
                <Requirements onBack={handleBackClick} type={RequirementTypeEnum.POA} />
            ) : (
                <React.Fragment>
                    <div className='main'>
                        <React.Fragment>
                            {isCameraMode ? (
                                <React.Fragment>
                                    {matchesMobileSm ? (
                                        <TakeDocumentMobile
                                            title={t('docVerificationWizard.proofAddress.title')}
                                            variant={CameraVariantLineEnum.POA}
                                            onClose={handleCloseCameraMode}
                                            onChangeFile={handleFilesChange}
                                            isUseNativeCamera={true}
                                        />
                                    ) : (
                                        <TakeDocument
                                            variant={CameraVariantLineEnum.POA}
                                            onClose={handleCloseCameraMode}
                                            onChangeFile={handleFilesChange}
                                            title={t('docVerificationWizard.proofAddress.title')}
                                        />
                                    )}
                                </React.Fragment>
                            ) : (
                                <div className='upload-container'>
                                    <Card
                                        title={t('docVerificationWizard.proofAddress.document')}
                                        onChangeFile={handleFilesChange}
                                        previews={previews}
                                        accept={{
                                            'image/*': ALLOWED_EXTENSIONS,
                                        }}
                                        description={t(
                                            'docVerificationWizard.filesUploaderDescriptionPoa',
                                        )}
                                        icon={
                                            <img
                                                src='https://static.dataspike.io/images/sdk/verification/poa-document.png'
                                                alt={t(
                                                    'docVerificationWizard.proofAddress.document',
                                                )}
                                                width={82}
                                                height={104}
                                            />
                                        }
                                        onRequirementsClick={handleRequirementsClick}
                                        isLoading={isLoading || isFilesLoading}
                                        isError={isError}
                                        isSuccess={isSuccess}
                                        message={files?.[0]?.status?.message}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    </div>
                    {!isCameraMode && (
                        <div className='actions'>
                            <Button
                                variant={isError ? 'error' : 'primary'}
                                onClick={handleNextClick}
                                size='xl'
                                className='button'
                            >
                                {isError ? t('common.redoPhoto') : t('common.takePhoto')}
                            </Button>
                            <React.Fragment>
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    name='image'
                                    accept='image/png,image/jpg,image/jpeg,application/pdf'
                                    multiple={false}
                                    onChange={handleFileChange}
                                    className='hidden'
                                />

                                <Button
                                    variant={isError ? 'error-secondary' : 'secondary'}
                                    onClick={handleUploadFileClick}
                                    size='xl'
                                    className='button'
                                    disabled={isLoading || isFilesLoading}
                                >
                                    {t('common.uploadFile')}
                                </Button>
                            </React.Fragment>
                        </div>
                    )}
                </React.Fragment>
            )}
        </AddressContainer>
    );
};
